/**
 * main react app loader
 *
 */
import React, { useEffect, useState } from "react";
import { hot } from "react-hot-loader/root";
import { useLocation } from "react-router-dom";
import styles from "./styles/app.module.scss";
import Router from "./Router";
import { AuthService } from "app/services";
import { useSelector, useStoreActions } from "./utils/hooks";
import { fetchSelf } from "./actions/user";
import { usePostViewsTracking } from "./utils/hooks/usePostViewsTracking";
import { GoogleOAuthProvider } from "@react-oauth/google";
import settings from "config/environment";
import { RootState } from "typedefs";
import { shallowEqual } from "react-redux";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  UserContextType,
  UserContext,
  RoleType,
} from "app/contexts/UserContext";
import { captureUTMParams } from "./utils/captureUTMParams";
import TimezonePopupModal from "app/components/TimezonePopupModal";
/***/
const Auth: any = AuthService;
const POST_VIEWS_POLL_INTERVAL = 5000; // 5 seconds

/**
 * Application Class
 */
function App() {
  const location = useLocation();
  const { sendAndClearPostViews } = usePostViewsTracking();
  const user = useSelector((state: RootState) => state.user, shallowEqual);
  const golfGeniusId = user.data?.golfgeniusId;

  // This identifies user for analytics.
  useEffect(() => {
    if (location.pathname !== "/signin") {
      Auth.loadAuthUser();
    }
  }, [Auth.isAuthenticated, golfGeniusId]);

  useEffect(() => {
    captureUTMParams();
  }, [location.pathname]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      sendAndClearPostViews();
    }, POST_VIEWS_POLL_INTERVAL);

    // The poll alone loses views recorded since its last tick when the tab
    // closes. `pagehide` covers the Safari/bfcache paths where
    // `visibilitychange` does not fire; both drain the same queue.
    const flushOnHide = () => {
      if (document.visibilityState === "hidden") {
        sendAndClearPostViews();
      }
    };

    document.addEventListener("visibilitychange", flushOnHide);
    window.addEventListener("pagehide", sendAndClearPostViews);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", flushOnHide);
      window.removeEventListener("pagehide", sendAndClearPostViews);
    };
  }, []);

  useStoreActions({ user: fetchSelf });

  const { self } = useSelector((state: RootState) => state.user);
  const [showTimezonePopup, setShowTimezonePopup] = useState(false);

  useEffect(() => {
    // Pre-app onboarding routes (signup steps, invite links, /trial/* and
    // /purchase/* plan checkouts) — defer the timezone popup until the user
    // lands on the main app, otherwise it overlays the signup questions and
    // plan selection. Only the plan-purchase pages are listed: /purchase/:id
    // (program checkout) keeps the popup as before.
    const onboardingPaths = [
      "/trial/",
      "/purchase/pro",
      "/purchase/academy",
      "/purchase/analyze",
      // The Academy onboarding wizard is a full-screen takeover; the popup
      // would sit over its first step.
      "/academy/wizard",
      "/signup",
      "/i/",
      "/invitation/",
      "/l/",
    ];
    if (onboardingPaths.some((path) => location.pathname.startsWith(path))) {
      // Also hide a popup already opened elsewhere — an SPA navigation into
      // a checkout must never keep it overlaid.
      setShowTimezonePopup(false);
      return;
    }
    if (self && self.timezonePopupShown === false) {
      setShowTimezonePopup(true);
    }
  }, [self?.id, self?.timezonePopupShown, location.pathname]);

  const currentUserRole: UserContextType = {};

  if (self) {
    currentUserRole.id = self.id;
    currentUserRole.kind = self.kind;
    currentUserRole.features = self.features;

    if (self.facilityMemberships && self.facilityMemberships.length > 0) {
      const currentFacilityMembership = self.facilityMemberships[0];
      const isAdminRole = currentFacilityMembership.role === RoleType.ADMIN;
      const isCoachAdminRole =
        currentFacilityMembership.role === RoleType.COACH &&
        currentFacilityMembership.isAdmin;
      currentUserRole.role = currentFacilityMembership.role;
      currentUserRole.facilityId = currentFacilityMembership.facilityId;
      currentUserRole.facility = self.scheduling?.facility;
      currentUserRole.facilityMembershipId = currentFacilityMembership.id;
      currentUserRole.isAdmin = currentFacilityMembership.isAdmin;

      currentUserRole.behavesLikeAdmin = isAdminRole || isCoachAdminRole;
      currentUserRole.facilityPermissions = currentFacilityMembership.permissions || [];
    } else {
      currentUserRole.role = self.kind as RoleType;
      currentUserRole.isAdmin = false;
      currentUserRole.behavesLikeAdmin = false;
    }
  }

  return (
    <div className={styles.app}>
      <GoogleOAuthProvider clientId={settings.GOOGLE_CLIENT_ID}>
        <UserContext.Provider value={currentUserRole}>
          <Router />
        </UserContext.Provider>
      </GoogleOAuthProvider>
      <ToastContainer />
      <TimezonePopupModal
        isOpen={showTimezonePopup}
        currentTimezone={self?.timezone}
        locale={self?.locale}
        onClose={() => setShowTimezonePopup(false)}
      />
    </div>
  );
}

export default hot(App);
